import { formatMoney, tonStringFromPounds } from '@/utils/NumericMutations'
import { utcToLocalDate, utcToCompanyOffsetDstSensitive } from '@/utils/DateFormatter'
import i18n from '@/i18n'

export default class AuditEntry {
  constructor (auditEntry, formatTree) {
    if (auditEntry.Version !== '1') throw new Error(`unknown version ${auditEntry.Version} for audit entry`)

    const formatter = getComponentSegmentFormat(auditEntry.Component.split('/').map(decodeURIComponent), formatTree)
    this.rowKey = auditEntry.RowKey
    this.from = auditEntry.From !== undefined ? formatter.formatter(auditEntry.From) : auditEntry.From
    this.to = auditEntry.To !== undefined ? formatter.formatter(auditEntry.To) : auditEntry.To
    this.opType = opTypeLabel(auditEntry.OpType)
    this.modifiedAt = new Date(Date.parse(auditEntry.ModifiedAt))
    this.modifiedBy = auditEntry.ModifiedBy
    this.modifiedReason = auditEntry.ModifiedReason
    this.component = decodeURIComponent(auditEntry.Component)
    this.componentSegments = formatter.segments
  }

  static create (auditEntry, entityType) {
    return new AuditEntry(auditEntry, formatTreesV1[entityType])
  }
}

const opTypeLabel = type => {
  switch (type) {
    case 'create':
    case 'add':
      return i18n.t('add')
    case 'update':
      return i18n.t('change')
    case 'delete':
    case 'remove':
      return i18n.t('remove')
    default:
      return type
  }
}

const formatBuilders = {
  named: (label, children, reprFormat, format) => ({ label: i18n.t(label), repr: true, children, reprFormat, format }),
  keyed: (label, children, format) => ({ label: i18n.t(label), repr: true, hideRepr: true, children, format }),
  composite: (label, children) => ({ label: i18n.t(label), children }),
  field: (label, format = undefined) => ({ label: i18n.t(label), format }),
  transparent: children => ({ transparent: true, children })
}

const formatters = {
  currency: formatMoney,
  miles: v => `${v} miles`,
  dateonly: d => utcToLocalDate(d),
  datetime: d => utcToLocalDate(d, 'MM/DD/YYYY hh:mm A'),
  dstSensitiveDatetime: d => {
    const adjusted = utcToCompanyOffsetDstSensitive(d)
    const hasTime = adjusted.hours() !== 0 || adjusted.minutes() !== 0
    return adjusted.format(hasTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY')
  },
  scheduledRateChangeStatus: v => {
    const match = typeof v === 'string' ? v.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s*\((.+)\)$/) : null
    if (match) {
      const adjusted = utcToCompanyOffsetDstSensitive(match[1])
      const hasTime = adjusted.hours() !== 0 || adjusted.minutes() !== 0
      const formatted = adjusted.format(hasTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY')
      return `${formatted} (${match[2]})`
    }
    return v
  },
  dueDiligenceApprovalStatus: v => {
    if (typeof v !== 'string') return v
    const wrapped = v.match(/^\d{4}-\d{2}-\d{2}T[\d:.]+Z?\s*\((.+)\)$/)
    const status = wrapped ? wrapped[1] : v
    return status.replace(/([a-z])([A-Z])/g, '$1 $2')
  },
  visitSummary: v => {
    const match = typeof v === 'string' ? v.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s*\((.+)\)$/) : null
    return match ? `${utcToLocalDate(match[1], 'MM/DD/YYYY hh:mm A')} (${match[2]})` : v
  },
  yesno: b => b ? i18n.t('yes') : i18n.t('no'),
  rawPercentage: b => `${b}%`,
  feet: b => `${b}"`,
  lbsToTons: lbs => tonStringFromPounds(lbs, 3)
}

const buildFormatTree = builderFn => builderFn(formatBuilders.named, formatBuilders.composite, formatBuilders.field, formatters, formatBuilders.transparent, formatBuilders.keyed)

const formatTreesV1 = buildFormatTree((named, composite, field, fmt, transparent, keyed) => ({
  contract: {
    activity: named('activity', {
      activityDetail: named('detail', {
        cost: composite('cost', {
          rate: field('rate', fmt.currency)
        }),
        product: field('product')
      }),
      baseCost: composite('baseCost', {
        rate: field('rate', fmt.currency),
        payOn: field('payOn'),
        payBy: field('payBy'),
        rateSheetLine: named('rateSheetLine', {
          rate: field('rate', fmt.currency)
        }),
        rateSheetLines: named('rateSheetLine', {
          rate: field('rate', fmt.currency),
          speciesGroup: field('speciesGroup'),
          lengthGroup: field('lengthGroup'),
          diameterGroup: field('diameterGroup'),
          grade: field('grade'),
          endUseSort: field('endUseSort'),
          isLocked: field('locked', fmt.yesno)
        })
      }),
      deductFromActivity: field('deductFromActivity'),
      counterparty: field('counterparty'),
      activityTemplate: field('activityTemplate'),
      effectiveDate: field('effectiveDate', fmt.dateonly)
    }),
    scheduledRateChange: named('scheduledRateChange', {
      status: field('status', fmt.scheduledRateChangeStatus)
    }, fmt.dstSensitiveDatetime, fmt.scheduledRateChangeStatus),
    distance: field('distance', fmt.miles),
    expiration: field('expirationDate', fmt.dateonly),
    effectiveDate: field('effectiveDate', fmt.dateonly),
    paused: field('paused', fmt.yesno),
    draft: field('draft', fmt.yesno),
    status: field('status'),
    approvalStatus: field('approvalStatus'),
    ticketBooks: field('ticketBooks')
  },
  activityTemplate: {
    baseCost: composite('baseCost', {
      rate: field('rate', fmt.currency),
      payBy: field('payBy'),
      payOn: field('payOn')
    }),
    details: named('detail', {
      product: field('product'),
      rate: field('rate', fmt.currency)
    }),
    rateModifier: field('modifier'),
    costType: field('costType'),
    glCode: field('glCode'),
    glOffset: field('glOffset'),
    productionType: field('productionCostType')
  },
  ticket: {
    inWeight: field('inWeight', fmt.lbsToTons),
    outWeight: field('outWeight', fmt.lbsToTons),
    defectWeight: field('defectWeight', fmt.lbsToTons),
    weighedInAt: field('weighedInAt', fmt.datetime),
    weighedOutAt: field('weighedOutAt', fmt.datetime),
    extTicketNumber1: field('extTicketNumber1'),
    extTicketNumber2: field('extTicketNumber2'),
    product: field('product'),
    contract: field('contract'),
    deck: field('deck'),
    transitStatus: field('transitStatus'),
    scalingStatus: field('scalingStatus'),
    seal: field('seal'),
    defects: named('defects', {
      quantity: field('quantity'),
      percentage: field('percentage', fmt.rawPercentage),
      diameter: field('diameter', fmt.feet),
      defectDefinition: field('defectDefinition')
    }),
    driver: field('driver'),
    unloaderOperation: transparent({
      unloadedAt: field('unloadedAt', fmt.datetime),
      unloadedBy: field('unloadedBy'),
      rolledOutAt: field('rolledOutAt', fmt.datetime),
      rolledOutBy: field('rolledOutBy'),
      rolloutCompletedAt: field('rolloutCompletedAt', fmt.datetime)
    })
  },
  tract: {
    tractCode: field('tractCode'),
    tractName: field('tractName'),
    cost: field('cost', fmt.currency),
    contractAccount: field('contractAccount'),
    hauler: field('hauler'),
    tractCertifications: field('tractCertifications'),
    defaultLogger: field('defaultLogger'),
    supplier: field('supplier'),
    consultingForester: field('consultingForester'),
    forester: field('forester'),
    tractType: field('tractType'),
    status: field('status'),
    scalingRegion: field('scalingRegion'),
    landowner: field('landOwner'),
    setting: field('setting'),
    purchaser: field('purchaser'),
    purchaseDate: field('purchaseDate', fmt.dateonly),
    harvestStartDate: field('harvestStart', fmt.dateonly),
    harvestEndDate: field('harvestEnd', fmt.dateonly),
    availabilityStartDate: field('availabilityStart', fmt.dateonly),
    availabilityEndDate: field('availabilityEnd', fmt.dateonly),
    tractLogger: field('loggers'),
    countrySubdivision: field('state'),
    countrySecondarySubdivision: field('county'),
    mapAndParcelNumber: field('mapAndParcelNumber'),
    acres: field('acres'),
    latitude: field('latitude'),
    longitude: field('longitude'),
    tractContents: named('cruise', {
      product: field('product'),
      species: field('species'),
      pricePerUnit: field('pricePerUnit', fmt.currency),
      adjustment: field('adjustment', fmt.lbsToTons),
      expected: field('expected', fmt.lbsToTons)
    }),
    tractUsers: field('tractUsers'),
    ticketBooks: field('ticketBooks'),
    dueDiligenceExemption: field('dueDiligenceExemption'),
    tractMitigationItems: named('mitigationGroup', {
      riskPresence: field('riskPresence'),
      mitigationComment: field('riskMitigationComments'),
      habitat: named('mitigationHabitat', {}),
      speciesHit: named('mitigationSpeciesHit', {})
    }),
    dueDiligenceApprovals: keyed('dueDiligenceApproval', {
      status: field('status', fmt.dueDiligenceApprovalStatus),
      rejectionComment: field('rejectionComment'),
      comment: field('reviewerComment')
    }, fmt.dueDiligenceApprovalStatus),
    tractVisits: keyed('visit', {
      visitType: field('visitType'),
      completedAt: field('completedAt', fmt.datetime),
      notes: field('notes'),
      tract: field('tract')
    }, fmt.visitSummary),
    bids: named('bid', {
      name: field('bidName'),
      supplier: field('supplier'),
      distance: field('distance'),
      harvestStartDate: field('harvestStart', fmt.dateonly),
      harvestEndDate: field('harvestEnd', fmt.dateonly),
      bidDate: field('bidDate', fmt.dateonly),
      bidExpiration: field('bidExpiration', fmt.dateonly),
      rfbStatus: field('rfbStatus'),
      outcome: field('outcome'),
      reason: field('reason'),
      tract: field('tract'),
      products: named('product', {
        deliveredRate: field('deliveredRate', fmt.currency),
        harvestVolume: field('volume'),
        status: field('status')
      })
    })
  },
  defectDefinition: {
    name: field('name'),
    defectLbs: field('defectTons', fmt.lbsToTons),
    defaultPercentage: field('defaultPercentage'),
    category: field('category')
  },
  diameterWeights: {
    diameterWeights: field('diameterWeights')
  },
  accountPayableTemplate: {
    advance: field('advance'),
    amount: field('amount', fmt.currency),
    startDate: field('startDate', fmt.dateonly),
    paymentNote: field('paymentTitle'),
    activityTemplate: field('activityTemplate'),
    paused: field('paused', fmt.yesno)
  }
}))

const getComponentSegmentFormat = (segments, formatTree) => {
  const fmt = formatComponentSegments(segments, formatTree)
  if (fmt === undefined) {
    console.warn(`Could not get formatting information for the following component string (check the audit data format tree):\n${segments.map(s => `'${s}'`).join(' > ')}`)
    return { formatter: s => s, segments }
  }
  return fmt
}

const formatComponentSegments = (segments, formatTree) => {
  if (segments.length === 0) {
    return {
      segments: [],
      formatter: undefined
    }
  }

  const fmt = formatTree?.[segments[0]]
  if (fmt === undefined) return undefined
  if (fmt.transparent) return formatComponentSegments(segments.slice(1), fmt.children)

  const seg = (fmt.repr && !fmt.hideRepr)
    ? [fmt.label, fmt.reprFormat ? fmt.reprFormat(segments[1]) : segments[1]]
    : [fmt.label]

  const remainingFormat = formatComponentSegments(segments.slice(fmt.repr ? 2 : 1), fmt.children)
  if (remainingFormat === undefined) return remainingFormat
  return {
    formatter: remainingFormat.formatter ?? fmt.format ?? (s => s),
    segments: [...seg, ...remainingFormat.segments]
  }
}
